/*
 * The signed-in panel: sidebar, top bar and the page on screen.
 *
 * Routing is the client app's (src/ui/Dashboard.tsx): the page is a `View`
 * held in state, mirrored into the address with history.pushState, and read
 * back on popstate, so Back/Forward and deep links (/wallet, /referrals …)
 * work without a router library.
 */

import { useEffect, useRef, useState } from "react";
import { useAuth, useMe } from "../auth/AuthContext";
import { Info, Inbox, LogOut, Menu, Settings, X, ChevronDown, Copy, Check, LayoutDashboard, UserPlus, Users, Receipt, Wallet, ArrowLeftRight, type Icon } from "./icons";
import { initials } from "./format";
import { copyText, type Notify } from "./bits";
import BrandMark from "./BrandMark";
import ReferSheet from "./ReferSheet";
import OverviewWorkspace from "./OverviewWorkspace";
import ReferralsWorkspace from "./ReferralsWorkspace";
import ClientsWorkspace from "./ClientsWorkspace";
import CommissionsWorkspace from "./CommissionsWorkspace";
import TransactionsWorkspace from "./TransactionsWorkspace";
import WalletWorkspace from "./WalletWorkspace";
import SettingsWorkspace from "./SettingsWorkspace";

export type View = "overview" | "referrals" | "clients" | "commissions" | "transactions" | "wallet" | "settings";

const PATHS: Record<View, string> = {
  overview: "/",
  referrals: "/referrals",
  clients: "/clients",
  commissions: "/commissions",
  transactions: "/transactions",
  wallet: "/wallet",
  settings: "/settings",
};

const LABELS: Record<View, string> = {
  overview: "Overview",
  referrals: "Referrals",
  clients: "Clients",
  commissions: "Commissions",
  transactions: "Transactions",
  wallet: "Wallet",
  settings: "Settings",
};

const NAV: { label: string; items: { view: View; icon: Icon }[] }[] = [
  {
    label: "Workspace",
    items: [
      { view: "overview", icon: LayoutDashboard },
      { view: "referrals", icon: UserPlus },
      { view: "clients", icon: Users },
      { view: "commissions", icon: Receipt },
      { view: "transactions", icon: ArrowLeftRight },
      { view: "wallet", icon: Wallet },
    ],
  },
  { label: "Account", items: [{ view: "settings", icon: Settings }] },
];

const PARTNERS_EMAIL = "info@wealthoak.ai";

function viewFromPath(path: string): View | null {
  const clean = path.replace(/\/+$/, "") || "/";
  const hit = (Object.keys(PATHS) as View[]).find((v) => PATHS[v] === clean);
  return hit ?? null;
}

export default function Shell() {
  const me = useMe();
  const { signOut } = useAuth();

  const [view, setView] = useState<View>(() => viewFromPath(window.location.pathname) ?? "overview");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [referOpen, setReferOpen] = useState(false);
  // Bumped after a referral is submitted, so the Referrals and Overview pages
  // on screen fetch again and show it.
  const [referralsVersion, setReferralsVersion] = useState(0);
  const [toast, setToast] = useState("");
  const [copied, setCopied] = useState(false);

  // An address this panel does not know becomes the Overview.
  useEffect(() => {
    if (!viewFromPath(window.location.pathname)) window.history.replaceState({}, "", "/");
    const onPop = () => setView(viewFromPath(window.location.pathname) ?? "overview");
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => {
    document.title = `${LABELS[view]} · Wealthoak Partner`;
  }, [view]);

  function navigate(next: View) {
    setView(next);
    setSidebarOpen(false);
    setProfileOpen(false);
    if (window.location.pathname !== PATHS[next]) window.history.pushState({}, "", PATHS[next]);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  // One timer for the one toast on screen, so a new toast is never cut short
  // by the previous one's timer.
  const toastTimer = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (toastTimer.current != null) window.clearTimeout(toastTimer.current);
    },
    [],
  );
  const notify: Notify = (message) => {
    if (toastTimer.current != null) window.clearTimeout(toastTimer.current);
    setToast(message);
    toastTimer.current = window.setTimeout(() => {
      toastTimer.current = null;
      setToast("");
    }, 2800);
  };

  // Escape closes the mobile drawer.
  useEffect(() => {
    if (!sidebarOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSidebarOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [sidebarOpen]);

  // The profile menu closes on a click elsewhere or Escape.
  const profileRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!profileOpen) return;
    profileRef.current?.querySelector<HTMLElement>(".profile-menu button")?.focus();
    const onDown = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) setProfileOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setProfileOpen(false);
        profileRef.current?.querySelector<HTMLElement>(".profile-button")?.focus();
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [profileOpen]);

  async function copyCode() {
    await copyText(me.code);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
    notify(`Partner code ${me.code} copied.`);
  }

  const openRefer = () => setReferOpen(true);
  const pending = me.status === "pending";

  return (
    <div className="dashboard-shell">
      <a className="skip-link" href="#main">
        Skip to content
      </a>

      {sidebarOpen && (
        <button className="sidebar-scrim" aria-label="Close navigation" onClick={() => setSidebarOpen(false)} />
      )}

      <aside className={`sidebar ${sidebarOpen ? "is-open" : ""}`} aria-label="Sidebar">
        <div className="sidebar__top">
          <a
            className="brand"
            href="/"
            aria-label="Wealthoak partner overview"
            onClick={(e) => {
              e.preventDefault();
              navigate("overview");
            }}
          >
            <BrandMark />
            <span className="brand__word">Wealthoak.ai</span>
            <span className="brand__tag">Partner</span>
          </a>
          <button className="icon-button sidebar__close" aria-label="Close navigation" onClick={() => setSidebarOpen(false)}>
            <X size={18} />
          </button>
        </div>

        <nav className="sidebar-nav" aria-label="Primary navigation">
          {NAV.map((section) => (
            <div className="sidebar-nav__section" key={section.label}>
              <p>{section.label}</p>
              {section.items.map(({ view: target, icon: Glyph }) => (
                <button
                  key={target}
                  className={`nav-item ${view === target ? "is-active" : ""}`}
                  aria-current={view === target ? "page" : undefined}
                  onClick={() => navigate(target)}
                >
                  <Glyph size={17} strokeWidth={1.8} />
                  <span>{LABELS[target]}</span>
                </button>
              ))}
            </div>
          ))}
        </nav>

        <div className="sidebar__help">
          <a href={`mailto:${PARTNERS_EMAIL}`}>
            <Inbox size={17} />
            {PARTNERS_EMAIL}
          </a>
          <p>Wealthoak Partners</p>
        </div>
      </aside>

      <div className="app-frame">
        <header className="topbar">
          <div className="topbar__left">
            <button className="icon-button menu-button" aria-label="Open navigation" onClick={() => setSidebarOpen(true)}>
              <Menu size={20} />
            </button>
            <div className="mobile-brand">
              <span>Wealthoak.ai</span>
              <span className="brand__tag">Partner</span>
            </div>
          </div>

          <div className="topbar__actions">
            <div className="code-chip" title="Your partner code: share it with the people you refer">
              <span>Partner code</span>
              <strong>{me.code}</strong>
              <button
                type="button"
                className={`icon-button ${copied ? "is-done" : ""}`}
                aria-label={copied ? "Partner code copied" : `Copy partner code ${me.code}`}
                onClick={() => void copyCode()}
              >
                {copied ? <Check size={15} /> : <Copy size={15} />}
              </button>
            </div>

            <div className="profile-wrap" ref={profileRef}>
              <button
                className="profile-button"
                aria-haspopup="menu"
                aria-expanded={profileOpen}
                aria-label={`Account menu for ${me.full_name}`}
                onClick={() => setProfileOpen((open) => !open)}
              >
                <span aria-hidden="true">{initials(me.full_name)}</span>
                <span className="profile-button__copy">
                  <strong>{me.full_name}</strong>
                  <small>{me.company_name || me.email}</small>
                </span>
                <ChevronDown size={15} />
              </button>
              {profileOpen && (
                <div className="profile-menu" role="menu" aria-label="Account">
                  <div className="profile-menu__who">
                    <strong>{me.full_name}</strong>
                    <small>{me.email}</small>
                  </div>
                  <button role="menuitem" onClick={() => navigate("settings")}>
                    <Settings size={15} />
                    Settings
                  </button>
                  <button role="menuitem" className="profile-menu__signout" onClick={() => void signOut()}>
                    <LogOut size={15} />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="dashboard-main" id="main" tabIndex={-1}>
          {pending && (
            <div className="notice is-warn" role="status">
              <Info size={16} />
              <span>
                <strong>Your partner account is awaiting approval.</strong> You can look around now; submitting
                referrals and withdrawing open once Wealthoak approves your account.
              </span>
            </div>
          )}

          {view === "referrals" ? (
            <ReferralsWorkspace key={referralsVersion} onRefer={openRefer} />
          ) : view === "clients" ? (
            <ClientsWorkspace onNavigate={navigate} />
          ) : view === "commissions" ? (
            <CommissionsWorkspace />
          ) : view === "transactions" ? (
            <TransactionsWorkspace />
          ) : view === "wallet" ? (
            <WalletWorkspace notify={notify} onNavigate={navigate} />
          ) : view === "settings" ? (
            <SettingsWorkspace notify={notify} />
          ) : (
            <OverviewWorkspace
              key={referralsVersion}
              onNavigate={navigate}
              onRefer={openRefer}
              contactEmail={PARTNERS_EMAIL}
            />
          )}
        </main>
      </div>

      {referOpen && (
        <ReferSheet
          onClose={() => setReferOpen(false)}
          onSubmitted={(name) => {
            setReferOpen(false);
            setReferralsVersion((n) => n + 1);
            notify(`Referral for ${name} submitted. Wealthoak will be in touch with them.`);
          }}
        />
      )}

      {toast && (
        <div className="toast" role="status" aria-live="polite">
          {toast}
        </div>
      )}
    </div>
  );
}
